import { ensureCognitoUser, requestMagicLink } from "@src/appV2/Auth/cognito/cognitoAuth";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";

interface SendLoginEmailParams {
  email: string;
  requestedURL?: string | undefined;
}

export type SendLoginEmailResult = { success: true } | { success: false; error: string };

/**
 * Sends a Cognito magic link login email.
 *
 * Calls `ensureCognitoUser` first so legacy workplace users (who exist in
 * `facilityusers` but not yet in the Cognito user pool) are JIT-imported
 * before the CUSTOM_AUTH flow runs. CUSTOM_AUTH does not invoke the
 * PreAuthentication Lambda, so the import has to be triggered from the
 * client — without this, pre-existing users hit "No registered user found"
 * on first login attempt in the redesigned app.
 */
export async function sendLoginEmail({
  email,
}: SendLoginEmailParams): Promise<SendLoginEmailResult> {
  try {
    await ensureCognitoUser(email);
    await requestMagicLink(email);
    return { success: true };
  } catch (error: unknown) {
    const mappedError = mapCognitoError(error, "magic-link-request");
    logError(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_FAILURE, {
      error,
      metadata: { errorCode: mappedError.code, errorMessage: mappedError.message, email },
    });
    return { success: false, error: mappedError.message };
  }
}
